import Component from "../component";
import loaderImage from "../../assets/loader.svg";
import { getLocale } from "../../helpers/locale";
import { getConfig } from "../../configuration/config";

export interface ITransitSummary {
    routes: woosmap.map.transit.TransitRoute[];
    travelMode: string;
    selectedRouteIndex: number;
    origin?: string;
    destination?: string;
    error?: string,
    isLoading: boolean
}

export enum TransitSummaryComponentEvents {
    ROUTE_INDEX_CHANGED = "route_index_changed",
    ROADBOOK_SHOW = "roadbook_show",
}


export default class TransitSummaryComponent extends Component<ITransitSummary> {
    init(): void {
        this.$element = <HTMLDivElement>document.createElement("div");
        this.$element.className = "routesSummary";
        this.$target.appendChild(this.$element);
    }

    render(): void {
        if (this.state && this.$element) {
            if (this.state.isLoading) {
                this.setLoading();
            } else if (this.state.routes && this.state.routes.length) {
                const transitSummary: HTMLDivElement[] = this.state.routes.map(
                    (route: woosmap.map.transit.TransitRoute, index: number) => {
                        const totalDistance = route.legs.reduce( ( sum, { distance } : { distance: number } ) => sum + distance , 0);
                        const startName =
                            route.legs.find(
                                (leg: woosmap.map.transit.TransitLeg) =>
                                    leg.start_location && leg.start_location.name !== null,
                            )?.start_location.name || "Walking";
                        const duration = `${route.duration}`
                        const travelModeIcon = getConfig().directions.travelModes.filter(({ modeKey }) => modeKey === this.state.travelMode)[0].darkIcon
                        const $transitSummary: HTMLDivElement = document.createElement("div");
                        $transitSummary.className =
                            index === this.state.selectedRouteIndex
                                ? "directionTrip directionTrip__selected"
                                : "directionTrip";
                        $transitSummary.dataset.index = index.toString();
                        $transitSummary.innerHTML = `
                        <img alt="${this.state.travelMode}" src="${travelModeIcon}" class="directionTrip__travelModeIcon">
                        <div class="directionTrip__description">
                            <div>
                                <div class="directionTrip__duration">${this.formatTime(parseInt(duration))}</div>
                                <div class="directionTrip__distance"> ${this.formatDistance(totalDistance)}</div>
                            </div>         
                        </div>`;
                        $transitSummary.appendChild(this.getHTMLForLegs(route));
                        $transitSummary.appendChild(this.getHTMLForDetailsLink());
                        $transitSummary.addEventListener("click", () => {
                            if ($transitSummary.dataset && $transitSummary.dataset.index) {
                                const routeIndex = Number($transitSummary.dataset.index)
                                if (routeIndex === this.state.selectedRouteIndex) {
                                    this.emit(TransitSummaryComponentEvents.ROADBOOK_SHOW);
                                } else {
                                    this.selectRoute(routeIndex);
                                }
                            }
                        });
                        return $transitSummary;
                    }
                );
                this.$element.replaceChildren(...transitSummary);
            } else {
                this.setEmptyResults();
            }
        }
    }

    setEmptyResults(): void {
        let messageBody;
        const $emptyResult: HTMLDivElement = document.createElement("div");
        messageBody = getLocale().directions.messages.initialBody;
        if (this.state.origin && this.state.destination) {
            messageBody = `${getLocale().directions.messages.error} 
                            ${getLocale().directions.messages.from} "<strong>${this.state.origin}</strong>" 
                            ${getLocale().directions.messages.to} "<strong>${this.state.destination}</strong>" 
                            ${getLocale().directions.messages.forTravelMode} <strong>${this.state.travelMode}</strong>`;
        }
        if (this.state.error) {
            messageBody += `<div class="directionTrip__error">${this.state.error}</div>`;
        }
        $emptyResult.innerHTML = `
                    <div class="directionTrip__empty">
                    <div>${messageBody}</div>
                    <div>`;
        this.$element.replaceChildren($emptyResult);
        if (this.state.travelMode !== 'TRANSIT') {
            $emptyResult.style.display = 'none';
        }
    }

    setLoading(): void {
        const $loader: HTMLDivElement = document.createElement("div");
        $loader.id = "loader";
        $loader.innerHTML = `<img alt="loading..." src=${loaderImage}>`
        this.$element.replaceChildren($loader);
    }

    selectRoute(routeIndex: number): void {
        this.setState(
            {
                selectedRouteIndex: routeIndex,
            },
            true,
            () => {
                const $routeSummary: HTMLDivElement = this.$element.querySelector(`[data-index="${this.state.selectedRouteIndex}"]`) as HTMLDivElement;
                document
                    .querySelectorAll(".directionTrip")
                    .forEach((el) => el.classList.remove("directionTrip__selected"));
                $routeSummary.classList.add("directionTrip__selected");
                this.emit(TransitSummaryComponentEvents.ROUTE_INDEX_CHANGED, this.state.selectedRouteIndex);
            }
        );
    }
    formatDistance(meters: number): string {
        if (meters < 1000) {
            return `${meters} m`;
        } else {
            return `${(meters / 1000).toFixed(2)} km`;
        }
    }

    formatTime(seconds: number): string {
        const minutes = Math.round(seconds / 60);
        if (minutes < 60) {
            return `${minutes} mins`;
        } else {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours} hours ${remainingMinutes} mins`;
        }
    }
    getHTMLForLegs(route: woosmap.map.transit.TransitRoute): HTMLDivElement {
        const $instructions: HTMLDivElement = document.createElement("div");
        $instructions.className = "directionTrip__summaryoverview";
        if (route.legs) {
            const $instructionsSteps: HTMLDivElement[] = route.legs.map((leg, index) => {
                const $stepContainer = document.createElement("div");
                $stepContainer.className = "step-container";
                const $step: HTMLDivElement = document.createElement("div");
                $step.className = `${leg.travel_mode === `pedestrian` ? "step" : "pill"}`
                $step.setAttribute("style", `background-color: ${leg.transport.color ? leg.transport.color : `#c0bfbf`}`);
                $step.innerHTML = `<span style="${leg.travel_mode === `pedestrian` ? 
                                   "background-color: transparent" : `background-color: ${leg.transport.color ? leg.transport.color : `#c0bfbf`}`}" 
                                   class="icon ${leg.travel_mode === `pedestrian` ? `pedestrian` 
                                    : this.getTransitMode(leg.transport.mode)}"> 
                                    </span><span class="transitduration">${leg.travel_mode === `transit` ? leg.transport.short_name ? leg.transport.short_name : `` : ``}</span>
                ${
                    leg.travel_mode === `pedestrian` ? `<span style="font-weight: bold">${this.formatTimeNumberOnly(leg.duration)}' ${index < route.legs.length - 1 ? '> ' : ''}   </span>` : ``} 
                `;
                $stepContainer.appendChild($step);
                leg.travel_mode === `transit` ? $step.insertAdjacentHTML('afterend', '<span style="font-weight: bold"> > </span>') : ''
                return $stepContainer;
                
            })
            $instructions.replaceChildren(...$instructionsSteps);
        }
        return $instructions;
    }
    getHTMLForDetailsLink() {
        const $details: HTMLDivElement = document.createElement("div");
        $details.className = "directionTrip__detailsMsg";
        $details.setAttribute("style", "margin-left:50px;margin-top:10px")
        $details.setAttribute("directionTrip__detailsMsg", "{{ rendererIndex }}");
        $details.innerHTML = `${getLocale().directions.messages.details}`;
        return $details;
    }
    getTransitMode(mode: string): string {
        const transitModes = {
            rail: [
                "highSpeedTrain",
                "intercityTrain",
                "interRegionalTrain",
                "regionalTrain",
                "cityTrain",
                "subway",
                "monorail",
                "inclined",
            ],
            tram: ["lightRail"],
            bus: ["bus", "privateBus", "busRapid"],
            ferries: ["ferry"],
            aerial: ["aerial", "flight", "spaceship"],
        };
        for (const key in transitModes) {
            if (transitModes[key].includes(mode)) {
                return key;
            }
        }
        return "pedestrian";
    }
    formatTimeNumberOnly(seconds: number): string {
        const minutes = Math.round(seconds / 60);
        return minutes;
        if (minutes < 60) {
            return `${minutes}`;
        } else {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours} ${remainingMinutes}`;
        }
    }
}
